// Özel lig sayfası teması: yönetim panelindeki renkler .alt-home-page
// değişkenlerine (src/pages/HomeAlt.css) ve üst menüye (SiteChrome.css)
// eşlenir. Sayfanın kendi bileşenleri değişmez; yalnız değişkenler ezilir.

const HEX = /^#[0-9a-f]{6}$/i;

const rgb = (hex) => {
  const value = HEX.test(hex || "") ? hex.slice(1) : "000000";
  return [0, 2, 4].map((i) => parseInt(value.slice(i, i + 2), 16)).join(", ");
};

const FONT_STACKS = {
  rounded: { display: '"Nunito", "Manrope", system-ui, sans-serif', body: '"Nunito", "Manrope", system-ui, sans-serif' },
  classic: { display: 'Georgia, "Times New Roman", serif', body: '"Manrope", system-ui, sans-serif' },
};

const RADIUS = { rounded: "26px", sharp: "4px" };

const paletteVars = (p, dark) => `
  --alt-bg: linear-gradient(180deg, ${p.background} 0%, ${p.backgroundAlt} 100%);
  --alt-bg-overlay: rgba(${rgb(p.primary)}, ${dark ? 0.16 : 0.1});
  --alt-text-primary: ${p.text};
  --alt-text-secondary: rgba(${rgb(p.text)}, 0.8);
  --alt-text-muted: rgba(${rgb(p.text)}, 0.58);
  --alt-heading-color: ${p.heading};
  --alt-accent: ${p.primary};
  --alt-accent-strong: ${p.primaryDark};
  --alt-accent-bright: ${p.primaryLight};
  --alt-gold: ${p.secondary};
  --alt-gold-soft: rgba(${rgb(p.secondary)}, ${dark ? 0.16 : 0.14});
  --alt-chip-bg: rgba(${rgb(p.primary)}, ${dark ? 0.22 : 0.12});
  --alt-chip-color: ${dark ? p.primaryLight : p.primary};
  --alt-surface-bg: ${p.surface};
  --alt-surface-subtle: rgba(${rgb(p.primary)}, 0.05);
  --alt-surface-tint: rgba(${rgb(p.primary)}, 0.06);
  --alt-surface-border: ${p.border};
  --alt-surface-border-strong: rgba(${rgb(p.primary)}, 0.5);
  --alt-surface-shadow-strong: 0 24px 60px rgba(${rgb(p.primary)}, 0.18), 0 6px 14px rgba(0, 0, 0, 0.06);
  --alt-nav-active-bg: linear-gradient(135deg, ${p.primaryLight}, ${p.primaryDark});
  --alt-state-bg: ${p.surface};
  --alt-state-color: ${p.text};
  --portal-header-from: ${p.headerFrom};
  --portal-header-to: ${p.headerTo};
  --el-gold: ${p.secondary};`;

// Liste ve ayrıntı sayfalarının kendi değişken aileleri (Results/Standings,
// Oyuncular, Maç, Takım, Oyuncu profili). Vurgu, zemin ve metin renkleri
// portal paletinden beslenir; durum renkleri (galibiyet/kart vb.) korunur.
const pageVars = (p, dark) => {
  const soft = `rgba(${rgb(p.primary)}, ${dark ? 0.16 : 0.1})`;
  return `
  --primary-color: ${p.primary};
  --players-primary: ${p.primary};
  --players-primary-soft: ${soft};
  --players-primary-border: rgba(${rgb(p.primary)}, 0.26);
  --players-bg: ${p.background};
  --players-surface: ${p.surface};
  --players-border: ${p.border};
  --players-text: ${p.text};
  --ap-primary: ${p.primary};
  --ap-primary-soft: ${soft};
  --tm-primary: ${p.primaryDark};
  --tm-primary-2: ${p.primary};
  --tm-purple-50: rgba(${rgb(p.primary)}, 0.06);
  --tm-purple-100: ${soft};
  --tm-purple-200: rgba(${rgb(p.primary)}, 0.28);
  --tm-purple-500: ${p.primary};
  --tm-gold: ${p.secondary};
  --tm-gold-soft: rgba(${rgb(p.secondary)}, 0.14);
  --tm-bg: ${p.background};
  --tm-bg-2: ${p.backgroundAlt};
  --tm-surface: ${p.surface};
  --tm-line: ${p.border};
  --tm-ink: ${p.text};
  --tm-ink-2: ${p.heading};
  --pp-purple: ${p.primaryDark};
  --pp-purple-bright: ${p.primary};
  --pp-purple-dark: ${p.primaryDark};
  --pp-purple-600: ${p.primary};
  --pp-purple-soft: ${soft};
  --pp-gold: ${p.secondary};
  --pp-gold-soft: rgba(${rgb(p.secondary)}, 0.14);
  --pp-bg: ${p.background};
  --pp-card-bg: ${p.surface};
  --pp-card-tint: rgba(${rgb(p.primary)}, 0.06);
  --pp-border: ${p.border};
  --pp-ink: ${p.text};`;
};

// Maç sayfası değişkenlerini :root'ta tanımlar ve koyu görünümü yoktur.
const matchVars = (p) => `
  --md-purple: ${p.primaryDark};
  --md-purple-bright: ${p.primary};
  --md-purple-dark: ${p.primaryDark};
  --md-purple-deep: ${p.headerTo};
  --md-purple-soft: rgba(${rgb(p.primary)}, 0.1);
  --md-surface-tint: rgba(${rgb(p.primary)}, 0.06);
  --md-gold: ${p.secondary};
  --md-gold-soft: rgba(${rgb(p.secondary)}, 0.14);
  --md-background: ${p.background};`;

/**
 * Portal teması için CSS. `html[data-portal="<slug>"]` altında geçerlidir;
 * sayfadan çıkılınca nitelik kaldırılır ve sitenin kendi teması döner.
 */
export function buildPortalCss(portal) {
  if (!portal?.slug || !portal.theme) return "";
  const scope = `html[data-portal="${String(portal.slug).replace(/[^a-z0-9-]/g, "")}"]`;
  const { mode = "light", light, dark } = portal.theme;
  const lightPalette = mode === "dark" ? dark : light;
  const darkPalette = mode === "light" ? light : dark;
  const layout = portal.layout || {};
  const font = FONT_STACKS[layout.fontStyle];
  const radius = RADIUS[layout.cornerStyle];

  return `
${scope} .alt-home-page {${paletteVars(lightPalette, mode === "dark")}
${font ? `  --alt-font-display: ${font.display};\n  --alt-font-body: ${font.body};\n  --el-font-display: ${font.display};\n  --el-font-body: ${font.body};` : ""}
}
${scope} .alt-home-page.is-dark {${paletteVars(darkPalette, mode !== "light")}
}
${radius ? `${scope} .alt-home-page { --ps-radius: ${radius}; }\n${scope} .alt-home-page :is(.alt-card, .portal-hero__stats div) { border-radius: ${radius}; }` : ""}
${scope} {${matchVars(lightPalette)}
}
${scope} :is(.ahr-page, .has-page, .alt-players-page, .tm-page, .player-page) {${pageVars(lightPalette, mode === "dark")}
}
${scope} :is(.alt-dark-theme .ahr-page, .alt-dark-theme .has-page, .alt-dark-theme .alt-players-page, .alt-players-page[data-theme="dark"], .alt-home-page.is-dark.alt-players-page, .tm-page[data-theme="dark"], .player-page.pp-dark) {${pageVars(darkPalette, mode !== "light")}
}
`;
}
